import React from 'react';
import { View } from 'react-native';
import { useNavigation } from '@react-navigation/native';
import { styled } from 'dripsy';
import { observer } from 'mobx-react-lite';
import { useTranslation } from 'react-i18next';

import {
  AuthFormLayout,
  AuthLayout,
  CheckBoxField,
  Field,
  Form,
  FormButton,
  Separator,
  TextTouchable,
  // AuthNavigationText,
} from '@components';
import { useAuthErrorHandler } from './hooks/useAuthErrorHandler';
import { useAuthModel } from '@models/AuthModel';
import {
  AuthNavigatorNavigationPropType,
  screens,
} from '@navigation';
import { IS_WEB } from '@utils';
import { signInSchema, SignInSchemaDataType } from '@validation';
import { useNavOptionsSet } from '@hooks';

const ForgotPasswordContainer = styled(View)({
  flexDirection: 'row',
  justifyContent: IS_WEB ? 'space-between' : 'flex-end',
  alignItems: 'center',
});

const ForgotPasswordText = styled(TextTouchable)({
  color: '$text400',
  fontWeight: 'semi',
  variant: 'text.medium',
});

type NavigationPropType = AuthNavigatorNavigationPropType<'SignIn'>;

export const SignInScreen = observer(() => {
  const nav = useNavigation<NavigationPropType>();
  const { t } = useTranslation();
  const { handleError } = useAuthErrorHandler();
  const authModel = useAuthModel();

  useNavOptionsSet({ title: t('signIn.title') });

  async function singIn(values: SignInSchemaDataType) {
    try {
      await authModel.signIn.run(values);

      authModel.authorize();
    } catch (error) {
      handleError({
        error,
        email: values.email,
      });
    }
  }

  function navigateToSignUp() {
    nav.navigate(screens.SignUp);
  }

  function navigateToForgotPassword() {
    nav.navigate(screens.ForgotPassword);
  }

  const initialValues: SignInSchemaDataType = {
    email: '',
    password: '',
    rememberMe: true,
  };

  return (
    <AuthLayout
      onPressHeaderButton={navigateToSignUp}
      headerButtonText={t('signIn.signUp')}
      headerButtonSideText={t('signIn.notMember')}
    >
      <AuthFormLayout title={t('signIn.title')}>
        <Form<SignInSchemaDataType>
          onSubmit={singIn}
          initialValues={initialValues}
          validationSchema={signInSchema}
          validateOnMount
        >
          <Field
            name="email"
            placeholder={t('signIn.email')}
            autoCapitalize="none"
            keyboardType="email-address"
          />
          <Separator />
          <Field
            name="password"
            placeholder={t('signIn.password')}
            autoCapitalize="none"
            secureTextEntry
          />
          <Separator />
          <ForgotPasswordContainer>
            {IS_WEB && (
              <CheckBoxField
                name="rememberMe"
                text={t('signIn.rememberMe')}
              />
            )}
            <ForgotPasswordText
              onPress={navigateToForgotPassword}
              hitSlop={20}
            >
              {t('signIn.forgotPassword')}
            </ForgotPasswordText>
          </ForgotPasswordContainer>
          <Separator />
          <FormButton>{t('signIn.signIn')}</FormButton>

          {/* {IS_MOBILE && (
            <Fragment>
              <Separator size="$5" />
              <AuthNavigationText
                onPress={navigateToSignUp}
                i18nKey="signIn.notMemberAndSignUp"
              />
            </Fragment>
          )} */}
        </Form>
      </AuthFormLayout>
    </AuthLayout>
  );
});
